import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useStore } from '../../store';
import {
  Alert,
  AlertDescription,
  Tabs,
  TabsCount,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@roger/ui-components';
import { AlertTriangle } from 'lucide-react';
import RoleManagementTab from './RoleManagementTab';
import PermissionManagementTab from './PermissionManagementTab';
import { PermissionSlug } from '../../constants/featureFlags';
import { listPermissions, listRoles } from '../../services/api';
import { formatTabCount } from '../../components/billing/formatTabCount';

type RBACTab = 'role-management' | 'permission-management';

const TABS: {
  id: RBACTab;
  label: string;
  countKey: 'roles' | 'permissions';
}[] = [
  { id: 'role-management', label: 'Role Management', countKey: 'roles' },
  {
    id: 'permission-management',
    label: 'Permission Management',
    countKey: 'permissions',
  },
];

type TabCounts = {
  roles: number | null;
  permissions: number | null;
};

const RolesManager: React.FC = () => {
  // Call hasPermission inside the selector so a mid-session super-user grant
  // clears the refusal card without a full reload (same shape as QidTag /
  // useShowEmrUserManagement — selecting the stable function alone would not).
  const isSuperUser = useStore(state =>
    state.hasPermission(PermissionSlug.ADMIN_SUPER_USER)
  );

  const [activeTab, setActiveTab] = useState<RBACTab>('role-management');
  const [tabCounts, setTabCounts] = useState<TabCounts | null>(null);
  const tabCountsGenerationRef = useRef(0);

  const fetchTabCounts = useCallback(async () => {
    if (!isSuperUser) return;
    const generation = ++tabCountsGenerationRef.current;
    const [rolesRes, permissionsRes] = await Promise.all([
      listRoles({ query: { limit: 500 } }),
      listPermissions({ query: { limit: 500 } }),
    ]);
    if (generation !== tabCountsGenerationRef.current) return;
    setTabCounts({
      roles: rolesRes.error || !rolesRes.data ? null : rolesRes.data.length,
      permissions:
        permissionsRes.error || !permissionsRes.data
          ? null
          : permissionsRes.data.length,
    });
  }, [isSuperUser]);

  useEffect(() => {
    void fetchTabCounts();
  }, [fetchTabCounts]);

  if (!isSuperUser) {
    return (
      <div>
        <Alert className='border-red-200 bg-red-50'>
          <AlertDescription className='text-red-800 flex gap-2 items-center'>
            <AlertTriangle className='h-4 w-4 text-red-600' />
            You do not have permission to view this page. Please contact your
            administrator if you need access.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <Tabs
        value={activeTab}
        onValueChange={value => setActiveTab(value as RBACTab)}
        className='flex w-full flex-col gap-6'
        variant='purple'
      >
        <TabsList className='flex w-fit max-w-full flex-wrap max-sm:h-auto'>
          {TABS.map(tab => (
            <TabsTrigger key={tab.id} value={tab.id}>
              <span className='inline-flex items-center gap-x-1'>
                <span>{tab.label}</span>
                <TabsCount
                  label={tab.label}
                  value={formatTabCount(tabCounts?.[tab.countKey])}
                />
              </span>
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value='role-management' className='m-0'>
          <RoleManagementTab />
        </TabsContent>

        <TabsContent value='permission-management' className='m-0'>
          <PermissionManagementTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default RolesManager;
